import { Injectable } from '@angular/core';

/** The header the token arrives and leaves on, matching VideoInterviewStepTokenFilter. */
export const VIDEO_STEP_TOKEN_HEADER = 'X-Video-Step-Token';

/** Per-tab, so a token cannot leak into another tab's funnel visit. */
const STORAGE_KEY = 'videoInterviewStepToken';

/**
 * Holds the video interview step token for this visit (BIZ-40996).
 *
 * The video step's proxy will not take a person id from the browser: it binds the talent to the
 * funnel's JWT, whose person id the server derived from an OTP-verified email. Three entry paths
 * have no such id — a brand-new applicant, whose JWT says -1 until the application creates their
 * person record; that same applicant coming back in a new tab within the cookie's life, where this
 * per-tab store starts empty (BIZ-41083); and a talent who followed a gather email and never logs
 * in — so the server hands each of them a signed step token on the response that established who
 * they are. This is where it waits until the video step calls.
 *
 * Kept in `sessionStorage` because the step runs on every page load and has to survive a refresh,
 * which an in-memory field would not. Per tab and cleared with it, which matches a credential that
 * is good for one step of one visit and expires in two hours server-side anyway; narrower than the
 * funnel's own login token, which lives in a readable cookie across tabs. Every access is guarded:
 * storage throws in a private window and can be disabled outright, and the step must fall back to
 * its deferred experience rather than break the funnel.
 */
@Injectable({ providedIn: 'root' })
export class VideoInterviewStepTokenService {

  /**
   * The authoritative value. Storage is read once, at construction, and written to afterwards —
   * never read again.
   *
   * <p>This getter used to fall back to re-reading storage whenever the field was null, which
   * quietly defeated `clear()`: if a browser allows reads but throws on removal, the old token came
   * straight back and the next person's video calls carried the previous person's credential. The
   * proxy refuses that pair, so it broke the step rather than exposing anything — but it broke
   * exactly the case `clear()` exists for.
   */
  private stepToken: string | null = null;

  constructor() {
    this.stepToken = this.read();
  }

  /** @return the token to send with the video interview calls, or null when we were given none. */
  get token(): string | null {
    return this.stepToken;
  }

  /**
   * Keeps a token the server just issued. Ignores a blank one, so a header that arrives empty
   * cannot discard a good token.
   */
  store(stepToken: string | null | undefined): void {
    if (stepToken == null || stepToken.trim().length === 0) {
      return;
    }
    this.stepToken = stepToken.trim();
    try {
      sessionStorage.setItem(STORAGE_KEY, this.stepToken);
    } catch (e) {
      // In memory only for this page view. The step still works until a refresh.
    }
  }

  /**
   * Forgets the token, for a talent starting again as somebody else. A token left over from a
   * previous person would disagree with the new login and the proxy would refuse the call, so this
   * matters for the funnel working, not only for tidiness.
   */
  clear(): void {
    this.stepToken = null;
    try {
      sessionStorage.removeItem(STORAGE_KEY);
    } catch (e) {
      // The field above is what the getter answers from, so this visit is already clear. What a
      // failed removal does leave is a stale value in storage that the NEXT page load would read.
      // Not worth more machinery: a browser that throws here almost certainly throws on write too,
      // and the proxy refuses a token that disagrees with the new sign-in, so the cost is the
      // step's deferred page rather than anything leaking.
    }
  }

  private read(): string | null {
    try {
      return sessionStorage.getItem(STORAGE_KEY);
    } catch (e) {
      return null;
    }
  }
}
